import { useQuery } from '@apollo/client';
import { Specialization_ProductVariant as SpecializationProductVariant } from '__generated__/graphql-types';
import type {
  Course_Course as Course,
  Partner_Partner as Partner,
  Specialization_Specialization as Specialization,
} from '__generated__/graphql-types-do-not-use';
import gql from 'graphql-tag';

import type {
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_courseMember_course as CourseMetadata,
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_courseMember as CourseMetadataType,
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType as Metadata,
  PrivateSessionDrawerMetadataQuery as PrivateSessionDrawerMetadataQueryData,
  PrivateSessionDrawerMetadataQueryVariables,
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_specializationMember_specialization as S12nMetadata,
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_specializationMember as S12nMetadataType,
  PrivateSessionDrawerMetadataQuery_XdpV1Resource_get_xdpMetadata_XdpV1_sdpMetadataMember as sdpMetadataMember,
} from 'bundles/enterprise-admin-drawers/components/__generated__/PrivateSessionDrawerMetadataQuery';
import PrivateDrawerCourseQuery from 'bundles/enterprise-admin-drawers/components/queries/PrivateDrawerCourse.graphql';
import PrivateDrawerSpecializationQuery from 'bundles/enterprise-admin-drawers/components/queries/PrivateDrawerSpecialization.graphql';
import type {
  PrivateDrawerCourseQuery as PrivateDrawerCourseQueryData,
  PrivateDrawerCourseQueryVariables,
} from 'bundles/enterprise-admin-drawers/components/queries/__generated__/PrivateDrawerCourse';
import type {
  PrivateDrawerSpecializationQuery as PrivateDrawerSpecializationQueryData,
  PrivateDrawerSpecializationQueryVariables,
} from 'bundles/enterprise-admin-drawers/components/queries/__generated__/PrivateDrawerSpecialization';
import { EnterpriseConfigurationCourseMetadataFragment } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonGraphqlQueries';

type Props = {
  isS12n: boolean;
  productId?: string;
  programId?: string;
  useAsDialogContent?: boolean;
};

export type PrivateSessionDrawerSpecialization = Pick<Specialization, 'id'> & {
  latestSpecialization: Pick<Specialization, 'id' | 'name' | 'cardImageUrl' | 'productVariant' | 'courseCount'> & {
    partners: Array<Pick<Partner, 'id' | 'name' | 'logo'>>;
    courses: Array<Pick<Course, 'id'>>;
  };
};

export type PropsFromUsePrivateSessionEditDrawerData = {
  course?:
    | (Pick<Course, 'id' | 'name' | 'promoPhotoUrl'> & {
        partners: Array<Pick<Partner, 'id' | 'name' | 'logo'>> | null;
      })
    | null;
  s12n?: PrivateSessionDrawerSpecialization | null;
  lenOfS12n?: number | null;
  isProfessionalCertificate?: boolean;
  isDrawerDataLoading?: boolean;
  metaData?: S12nMetadata | CourseMetadata;
};

export const PrivateSessionDrawerMetadataQuery = gql`
query PrivateSessionDrawerMetadataQuery($configurationScopeId: String!, $shouldIncludeXdpQuery: Boolean!, $sdpId: String!) {
  EnterpriseProductConfigurationMetadataV1Resource {
    get(id: $configurationScopeId) {
      ...EnterpriseConfigurationMetadataFragment
    }
  }

  XdpV1Resource @include(if: $shouldIncludeXdpQuery) {
    get(id: $sdpId) {
      id
      xdpMetadata {
        ... on XdpV1_sdpMetadataMember {
          sdpMetadata {
            id
            avgLearningMonthsAdjusted
          }
        }
      }
    }
  }
}
${EnterpriseConfigurationCourseMetadataFragment},
`;

const usePrivateSessionEditDrawerData = ({ isS12n, productId, programId, useAsDialogContent }: Props) => {
  const { data: courseData, loading: isCourseDataLoading } = useQuery<
    PrivateDrawerCourseQueryData,
    PrivateDrawerCourseQueryVariables
  >(PrivateDrawerCourseQuery, {
    variables: {
      id: productId!,
    },
    context: {
      clientName: 'gatewayGql',
    },
    // The course info is not used in the dialog version
    skip: isS12n || !productId || useAsDialogContent,
  });

  const { data: s12nData, loading: isS12nDataLoading } = useQuery<
    PrivateDrawerSpecializationQueryData,
    PrivateDrawerSpecializationQueryVariables
  >(PrivateDrawerSpecializationQuery, {
    variables: {
      ids: [productId!],
    },
    context: {
      clientName: 'gatewayGql',
    },
    // Most of the info isn't used in the dialog version, but we still need the course ids from the s12n
    skip: !isS12n || !productId,
  });

  const { data: metadata, loading: isMetadataLoading } = useQuery<
    PrivateSessionDrawerMetadataQueryData,
    PrivateSessionDrawerMetadataQueryVariables
  >(PrivateSessionDrawerMetadataQuery, {
    variables: {
      configurationScopeId: isS12n
        ? `Program!~${programId}~Specialization!~${productId}`
        : `Program!~${programId}~VerifiedCertificate!~${productId}`,
      sdpId: isS12n ? `SPECIALIZATION~${productId}` : '',
      shouldIncludeXdpQuery: isS12n && !useAsDialogContent,
    },
    skip: !productId || !programId,
  });

  const formatProductMetaData = (metadataType?: Metadata) => {
    if (isS12n) {
      return (metadataType as S12nMetadataType)?.specialization;
    } else {
      return (metadataType as CourseMetadataType)?.course;
    }
  };

  const s12n = s12nData?.Specialization?.queryLatestVersionByIds?.[0];

  return {
    course: courseData?.Course?.queryById,
    s12n,
    lenOfS12n: (metadata?.XdpV1Resource?.get?.xdpMetadata as sdpMetadataMember)?.sdpMetadata?.avgLearningMonthsAdjusted,
    isProfessionalCertificate:
      s12n?.latestSpecialization?.productVariant === SpecializationProductVariant.ProfessionalCertificateS12N,
    isDrawerDataLoading: isCourseDataLoading || isS12nDataLoading || isMetadataLoading,
    metaData: formatProductMetaData(metadata?.EnterpriseProductConfigurationMetadataV1Resource?.get?.metadataType),
  };
};

export default usePrivateSessionEditDrawerData;
